<script>
    import {PATH_PREFIX} from '../../apiUrls.js';

    import {fade} from 'svelte/transition';
    import {paginate} from '../common/pagination.js';
    import DataTable from '../DataGrid/DataTable.svelte';
    import DateTimeColumn from '../DataGrid/ColumnRenderers/DateTimeColumn.svelte';
    import ActivityVersionColumn from './ElementView/ColumnRenderers/ActivityVersionColumn.svelte';
    import BranchColumn from './ElementView/ColumnRenderers/BranchColumn.svelte';
    import ElementVersionNameColumn from './ElementView/ColumnRenderers/ElementVersionNameColumn.svelte';
    import WorkSequenceColumn from './ElementView/ColumnRenderers/WorkSequenceColumn.svelte';
    import ElementUsageActionsColumn from './ElementView/ColumnRenderers/ElementUsageActionsColumn.svelte';

    import {elementDetails, standaloneEdit} from './workspacesStore.js';
    import {get} from '../../utils.js';
    import {sgettext} from '../../i18n.js';

    export let details = $elementDetails;
    let loading = false;
    let draftVersionNumber = 0;
    let showStatus = true;

    $: if($elementDetails){
        details = $elementDetails;
        getUsage();
    }

    let PER_PAGE = 10;

    let data = {
        templates: {
            columns: {
                activity_version_number: {
                    label: sgettext('$$ACTIVITY_VERSION$$'),
                    sourceObjectName: 'activity_version',
                    keyPath: 'version_number'
                },
                activity_version: {
                    label: sgettext('$$ACTIVITY_1$$'),
                    renderer: ActivityVersionColumn,
                },
                template_status: {
                    label: sgettext('$$STATUS$$'),
                    sourceObjectName: 'activity_version',
                    min_width: '80px'
                },
                branch: {
                    label: sgettext('$$BRANCH$$'),
                    renderer: BranchColumn,
                },
                version_number: {
                    label: sgettext('$$ELEMENT_VERSION$$'),
                },
                name: {
                    label: sgettext('$$WORK_SEQUENCE$$'),
                    renderer: WorkSequenceColumn
                },
                last_modified_date: {
                    label: sgettext('$$LAST_MODIFIED$$'),
                    renderer: DateTimeColumn,
                    sourceObjectName: 'activity_version'
                },
                actions: {
                    label: '',
                    renderer: ElementUsageActionsColumn
                }
            },
            rows: [],
            page: [],
            per_page: PER_PAGE,
        },
        instances: {
            columns: {
                activity_version: {
                    label: sgettext('$$ACTIVITY_1$$'),
                    renderer: ActivityVersionColumn,
                },
                branch: {
                    label: sgettext('$$BRANCH$$'),
                    renderer: BranchColumn,
                },

                version_number: {
                    label: sgettext('$$ELEMENT_VERSION$$'),
                },
                name: {
                    label: sgettext('$$WORK_SEQUENCE$$'),
                    renderer: WorkSequenceColumn,
                },
                scheduled_start_time: {
                    label: sgettext('$$SCHEDULED$$'),
                    sourceObjectName: 'activity_version',
                    renderer: DateTimeColumn
                },
                sap_work_order_number: {
                    label: sgettext('$$WORK_ORDER$$'),
                    sourceObjectName: 'activity_version'
                },
                created_date: {
                    label: sgettext('$$CREATED$$'),
                    sourceObjectName: 'activity_version',
                    renderer: DateTimeColumn
                }
            },
            rows: [],
            page: [],
            per_page: PER_PAGE,
        },
        related_elements: {
            columns: {
                version_number: {
                    label: sgettext('$$VERSION$$'),
                    sourceObjectName: 'element_version',
                },
                name: {
                    label: sgettext('$$NAME$$'),
                    renderer: ElementVersionNameColumn,
                },
                branch: {
                    label: sgettext('$$BRANCH$$'),
                    renderer: BranchColumn,
                    sourceObjectName: 'element',
                },
                created_date: {
                    label: sgettext('$$CREATED$$'),
                    renderer: DateTimeColumn
                }

            },
            rows: [],
            page: [],
            per_page: PER_PAGE,
        },
    };

    let draftElementVersionViewApiUrl = `${PATH_PREFIX}/sw/api/elements_database/draft_element_version/`;

    async function getUsageInTemplates() {
        const eevId = $elementDetails ? $elementDetails.eevId : 0;

        if (eevId) {
            const templatesResponse = await get(`${PATH_PREFIX}/sw/api/elements_database/element_version/${eevId}/usage-in-activities/?role__activity__template=true`);

            if (templatesResponse.ok) {
                data.templates.rows = await templatesResponse.json();
                data.templates.page = paginate(data.templates.rows, data.templates.per_page, 1);
            }
        }
    }

    async function getUsageInInstances() {
        const eevId = $elementDetails ? $elementDetails.eevId : 0;

        if (eevId) {
            const instancesResponse = await get(`${PATH_PREFIX}/sw/api/elements_database/element_version/${eevId}/usage-in-activities/?role__activity__template=false`);

            if (instancesResponse.ok) {
                data.instances.rows = await instancesResponse.json();
                data.instances.page = paginate(data.instances.rows, data.instances.per_page, 1);
            }
        }
    }

    async function getRelatedElements() {
        const eevId = $elementDetails ? $elementDetails.eevId : 0;

        if (eevId) {
            const relatedElementsResponse = await get(`${PATH_PREFIX}/sw/api/elements_database/element_version/${eevId}/related-elements/`);
            if (relatedElementsResponse.ok) {
                data.related_elements.rows = await relatedElementsResponse.json();
                data.related_elements.page = paginate(data.related_elements.rows, data.related_elements.per_page, 1);
            }
        }
    }

    async function getUsage() {
        loading = true;
        await getUsageInTemplates();
        getUsageInInstances();
        getRelatedElements();
        loading = false;
    }

</script>

{#if details}
    {#if $standaloneEdit}
        <div class="title">
            <h3 class="text-primary">
                {details.name}
            </h3>
        </div>
    {/if}
    {#if loading}
        <i class="fa fa-spinner fa-spin"></i>
    {:else}
        <h4 transition:fade|local>{sgettext('$$TEMPLATES$$')}</h4>
        <DataTable
            small={false}
            showFooter={data.templates.rows.length > data.templates.per_page}
            footerColSpan={Object.keys(data.templates.columns).length}
            columns={data.templates.columns}
            rows={data.templates.page}
            total={data.templates.rows.length}
            perPage={data.templates.per_page}
            changePage={ (e) => data.templates.page = paginate(data.templates.rows, data.templates.per_page, e.page)}
            {showStatus}

        />

        <br/>

        <h4 transition:fade|local>{sgettext('$$INSTANCES$$')}</h4>
        <DataTable
            small={false}
            showFooter={data.instances.rows.length > data.instances.per_page}
            footerColSpan={Object.keys(data.instances.columns).length}
            columns={data.instances.columns}
            rows={data.instances.page}
            total={data.instances.rows.length}
            perPage={data.instances.per_page}
            changePage={ (e) => data.instances.page = paginate(data.instances.rows, data.instances.per_page, e.page) }
        />

        <br/>

        <h4 transition:fade|local>{sgettext('$$RELATED_ELEMENTS$$')}</h4>
        <DataTable
            small={false}
            showFooter={data.related_elements.rows.length > data.related_elements.per_page}
            footerColSpan={Object.keys(data.related_elements.columns).length}
            columns={data.related_elements.columns}
            rows={data.related_elements.page}
            total={data.related_elements.rows.length}
            perPage={data.related_elements.per_page}
            changePage={ (e) => data.related_elements.page = paginate(data.related_elements.rows, data.related_elements.per_page, e.page) }
        />
    {/if}
{/if}
